import { useRequest } from 'ahooks';
import { Descriptions, Popover, Table } from 'antd';
import LocaleContext from 'locales';
import moment from 'moment';
import { displaySize } from 'pages/utils';
import React, { useContext, useEffect, useState } from 'react';
import { getOwnVdsOfUser, getVdsOfUser } from 'services/websocket/users';

/**
 * 컴포넌트: 보유 VD 목록
 * @param {*} readOnly 읽기 전용
 * @param {string} user 사용자 ID
 * @param {string} requestId 서비스 신청 ID
 * @param {string} apprType 서비스 신청 타입
 * @param {[]} contents 읽기 모드 데이터
 * @param {function} [onSelect] 목록 선택 시 콜백 함수
 * @param {*} props
 * @returns ReactNode
 */
function OwnDesktops(props) {
  const { readOnly, user, requestId, apprType, contents, onSelect } = props;

  // hooks
  const { locale } = useContext(LocaleContext);

  // states
  const [dataSource, setDataSource] = useState([]); // Table.dataSource
  const [defaultSelectKey, setDefaultSelectKey] = useState([]); // Table Row 선택 키 목록

  // VD 생성일때 호출
  const fetchGetOwnVdsOfUser = useRequest(getOwnVdsOfUser, {
    manual: true,
    defaultParams: [user],
    refreshDeps: [user],
    onSuccess: (data) => setDataSource(data.data),
  });

  // VD 생성이 아닌 경우 호출
  const fetchGetVdsOfUser = useRequest(getVdsOfUser, {
    manual: true,
    defaultParams: [user, requestId],
    refreshDeps: [user],
    onSuccess: (data) => setDataSource(data.data),
  });

  useEffect(() => {
    if (contents) {
      setDataSource(contents);
    }
  }, [contents]);

  // 사용자가 바뀔 때마다 재호출한다.
  useEffect(() => {
    if (apprType === 'approval.types.create-vd') {
      fetchGetOwnVdsOfUser.runAsync(user);
    }
    if (!readOnly) {
      fetchGetVdsOfUser.runAsync(user, requestId);
    }
  }, [user]);

  return (
    <Table
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={dataSource}
      columns={[
        {
          title: locale.resource['txt.desktop-group'],
          dataIndex: 'vdGroupName',
          ellipsis: true,
          render: (text, record) => `${text}( ${record.vdName} )`,
        },
        {
          title: locale.resource['txt.os'],
          ellipsis: true,
          dataIndex: 'os',
          width: 100,
        },
        {
          title: locale.resource['txt.flavor'],
          dataIndex: 'flavorName',
          ellipsis: true,
          render: (text, record) => {
            const gpu = record.extraSpecs?.['resources:VGPU'];
            const { size, unit } = displaySize(record.ram);
            return (
              <Popover
                title={locale.resource['txt.flavor-info']}
                overlayStyle={{ width: 140 }}
                content={
                  <Descriptions size="small" column={1}>
                    <Descriptions.Item label="CPU">
                      {record.cpus ?? '-'}
                      {locale.resource['txt.core']}
                    </Descriptions.Item>
                    <Descriptions.Item label="RAM">
                      {record.ram ? `${size}${unit}` : '-'}
                    </Descriptions.Item>
                    {gpu && (
                      <Descriptions.Item label="GPU">
                        {locale.resource['txt.use']}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                }
              >
                {text}
              </Popover>
            );
          },
        },
        {
          title: locale.resource['txt.disk'],
          dataIndex: 'disk',
          width: 50,
          align: 'right',
        },
        {
          title: locale.resource['txt.created-date'],
          dataIndex: 'createDate',
          width: 140,
          render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
        },
      ]}
      rowSelection={
        readOnly
          ? false
          : {
              type: 'checkbox',
              selectedRowKeys: defaultSelectKey,
              onChange: (key, record) => {
                setDefaultSelectKey(key);
                if (onSelect) {
                  onSelect(record);
                }
              },
            }
      }
    />
  );
}

export default OwnDesktops;
